import { useMessagePlacementHooks } from '@atlassian/post-office-context';
import { createPlacementComponent } from '@post-office/placement-common/utils/create-placement-component';
import { MaybeSuspenseBoundary } from '@post-office/placement-common/utils/message-renderer';
import { useInitialData } from '@post-office/placement-contexts/hooks';
import { memo, type ReactNode, useCallback, useEffect, useState } from 'react';

import { MessageRenderer } from '../__generated__/client/render';
import { PLACEMENT_ID } from '../__generated__/common';
import { type JiraSideNavInitialData } from '../api/types';
import { type Placement as JiraSideNavPlacement } from '../contracts/Placement';
import { getMessage, type Message } from '../utils/get-message';
import { type ErrorBoundariedProps } from '@post-office/placement-contexts/create-placement-component/types';

export const JiraSideNavPlacementComponent: (
	props: ErrorBoundariedProps<JiraSideNavPlacement>,
) => ReactNode = createPlacementComponent<JiraSideNavPlacement>(
	PLACEMENT_ID,
	memo(({ initialData, preloadedInitialData, handleClearPreloadedData }) => {
		const { data, isLoading } = useInitialData<JiraSideNavInitialData>(initialData);
		// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents, @typescript-eslint/no-unsafe-assignment
		const [{ message, status }, setMessageState] = useState<{
			// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
			message: Message | undefined;
			status: 'loading' | 'done';
		}>(() => {
			if (preloadedInitialData) {
				return preloadedInitialData?.messages?.length > 0
					? {
							// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
							message: getMessage(preloadedInitialData.messages) as Message,
							status: 'done',
						}
					: { message: undefined, status: 'done' };
			}

			return { message: undefined, status: 'loading' };
		});

		const handleOnDismissed = useCallback(() => {
			setMessageState({ message: undefined, status: 'done' });

			if (typeof handleClearPreloadedData === 'function') {
				handleClearPreloadedData();
			}
		}, [handleClearPreloadedData]);

		const { onMessageDismiss, onMessageClicked, onMessageSnooze } = useMessagePlacementHooks();

		useEffect(() => onMessageDismiss(handleOnDismissed), [onMessageDismiss, handleOnDismissed]);
		useEffect(() => onMessageClicked(handleOnDismissed), [onMessageClicked, handleOnDismissed]);
		useEffect(() => onMessageSnooze(handleOnDismissed), [onMessageSnooze, handleOnDismissed]);

		const isDone = status === 'done';

		useEffect(() => {
			if (!isLoading && !isDone) {
				setMessageState({
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
					message: getMessage(data?.messages || []),
					status: 'done',
				});
			}
		}, [data, isLoading, isDone]);

		if (status === 'loading' || !message) {
			return null;
		}

		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		return (
			<MessageRenderer
				{...message}
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
				id={message.messageInstanceId}
				onDismissed={handleOnDismissed}
				SuspenseBoundary={MaybeSuspenseBoundary}
			/>
		);
	}),
	{
		choreographer: {
			isDefaultEnabled: false,
		},
	},
);

export default JiraSideNavPlacementComponent;
